import { styles } from '../components/shared/tables/styles';
import { getFlagLabel, getRuleFlag, OBSOLESCENCE_VALUE, PRESENCE_VALUE, RULE_VALUE } from '../helpers';
import { getApplicationName } from '../helpers/result';
import {
  ApplicationResultValue,
  CostResultKey,
  IApplicationComponentRef,
  IApplicationFull,
  IApplicationResult,
  IApplicationScopeRef,
  IFilterResult,
  IGroupedApplications,
  IInformationFormat,
  IInfrastructureComponent,
  INode,
  INotifications,
  IRemediationComponent,
  IUser,
  NotificationType,
  NovaCompatibility,
  RecommendationStatus,
} from '../types';
import { NorthStarPath } from '../types/north-star-target.type';
import { capitalizeEachFirstLetter, capitalizeFirstLetter, formatPresenceValue, formatPrice } from './string';

const TO_BE_COMPLETED = 'To be completed';

type CellType = 'currency' | 'value' | 'presence' | 'rule' | 'percentage';

export const getCellInfo = (
  resultValue: string,
  resultType: string,
  isCurrency: boolean
): { cellType: CellType; formattedValue: string | undefined } => {
  const isValue = resultType === 'value';
  const isPresence = resultType === 'presence';
  const isPercentage = resultType === 'percentage';
  const isNA = resultValue === 'n/a';
  const hasNoData = resultValue === 'n/d';
  const hasData = typeof resultValue !== 'undefined' && !isNA && !hasNoData;

  const getCellType = (): CellType => {
    if (hasData) {
      if (isCurrency) {
        return 'currency';
      } else if (isValue) {
        return 'value';
      } else if (isPresence) {
        return 'presence';
      } else if (isPercentage) {
        return 'percentage';
      }
    }
    return 'rule';
  };

  const getCellValue = (): string | undefined => {
    switch (getCellType()) {
      case 'currency': {
        const numericalValue = Number(resultValue);
        return formatPrice(numericalValue);
      }
      case 'value': {
        return resultValue;
      }
      case 'rule': {
        const flag = getRuleFlag(resultValue);
        return getFlagLabel(flag);
      }
      case 'presence': {
        return formatPresenceValue(resultValue);
      }
      case 'percentage': {
        return `${resultValue} %`;
      }
      default: {
        return undefined;
      }
    }
  };

  return { cellType: getCellType(), formattedValue: getCellValue() };
};

export const getColorStatus = (status: RecommendationStatus | undefined): string => {
  switch (status) {
    case 'processed-with-roadmap':
    case 'processed-without-remediation': {
      return 'success';
    }
    case 'processed-with-exception':
    case 'being-processed':
    case 'waiting-decommissioning': {
      return 'warning';
    }
    case 'active':
    case 'to-be-processed-missing-linked-assets':
    case 'not-processed': {
      return 'danger';
    }
    default: {
      return '';
    }
  }
};

export const getNotificationByType = (myNotifications: INotifications | undefined, type: NotificationType): number => {
  switch (type) {
    case 'claims': {
      return myNotifications ? myNotifications.claimsRequested : 0;
    }
    case 'roadmaps': {
      return myNotifications ? myNotifications.roadmapsOutdated : 0;
    }
    case 'recommendation': {
      return myNotifications ? myNotifications.recommendation : 0;
    }
    default: {
      return 0;
    }
  }
};

export const getRecommendationStatus = (status?: RecommendationStatus): string => {
  switch (status) {
    case 'active': {
      return 'Not planned';
    }
    case 'processed-with-roadmap': {
      return 'Done';
    }
    case 'processed-with-exception': {
      return 'Derogated';
    }
    case 'waiting-decommissioning': {
      return 'Waiting decommissioning';
    }
    case 'processed-without-remediation': {
      return 'Done (without remediation)';
    }
    case 'not-processed': {
      return 'Remediation outdated';
    }
    case 'being-processed': {
      return 'Being processed';
    }
    case 'to-be-processed-missing-linked-assets': {
      return 'Not planned (missing linked assets)';
    }
    default: {
      return '-';
    }
  }
};

export const getSearchMode = (search: string): string => {
  switch (search) {
    case '<': {
      return 'Below';
    }
    case '>': {
      return 'Higher';
    }
    case '=': {
      return 'Equal';
    }
    default: {
      return 'Equal';
    }
  }
};

export const formatData = (value: string): string => {
  return value.replace(/[{}"]/g, '');
};

export const getBackgroundColor = (key: ApplicationResultValue): string => {
  switch (key) {
    case 'good':
      return 'bg-success bg-opacity-10 text-success';
    case 'medium':
      return 'bg-warning bg-opacity-10 text-warning';
    case 'bad':
      return 'bg-danger bg-opacity-10 text-danger';
    case 'n/a':
      return 'bg-light bgn-opacity-10 text-secondary';
    case 'n/d':
      return 'text-secondary';
    default:
      return 'text-primary';
  }
};

export const getBadgeColor = (key: ApplicationResultValue): string => {
  switch (key) {
    case 'good':
    case 'intermediate':
    case NorthStarPath.ALREADY_AZURE:
      return 'badge-discreet-success';
    case 'medium':
      return 'badge-discreet-warning';
    case 'bad':
      return 'badge-discreet-danger';
    case 'n/a':
      return 'badge-discreet-dark';
    case 'n/d':
      return 'badge-outline-secondary';
    default:
      return 'badge-discreet-primary';
  }
};

const computeRelations = (parentNode: IInformationFormat, childs: string[]) => {
  const firstChild = childs.shift();

  if (parentNode.children.find((value) => value.key === firstChild) === undefined) {
    // Create node if it doesn't exist
    parentNode.children.push({
      key: firstChild!,
      children: [],
    });
  }

  if (childs.length) {
    computeRelations(parentNode.children.find((value) => value.key === firstChild)!, childs);
  }
};

export const computeRecursiveData = (entries: string[]): IInformationFormat[] => {
  const nodes: IInformationFormat[] = [];

  for (const entry of entries.filter((e) => !!e)) {
    const levels = entry.split('/');
    const parent = levels.shift();

    // Create parent node if it doesn't exist
    if (nodes.find((value) => value.key === parent) === undefined) {
      nodes.push({
        key: parent!,
        children: [],
      });
    }

    computeRelations(nodes.find((value) => value.key === parent)!, levels);
  }

  return nodes;
};

export const computeMyApplications = (
  applicationsMine: IApplicationFull[] | undefined
): IGroupedApplications[] | undefined => {
  return (applicationsMine || [])
    .reduce((acc: IGroupedApplications[], curr) => {
      const accResults = acc.find((o) => o.applicationName === getApplicationName(curr));
      if (accResults) {
        return [
          ...acc.filter((o) => o.applicationName !== getApplicationName(curr)),
          { ...accResults, results: [...accResults.results, curr] },
        ];
      } else {
        return [
          ...acc,
          {
            applicationName: getApplicationName(curr),
            results: [curr],
          },
        ];
      }
    }, [])
    .filter(({ applicationName }) => applicationName !== 'undefined');
};

export const isUserOwner = (owners: IUser[] | undefined, userSub: string): boolean => {
  return owners ? owners.map((user) => user.email).includes(userSub) : false;
};

export const getHeaderCostWidth = (value: string) => {
  switch (value) {
    case 'Category':
    case 'Sub category':
      return styles.colWidth.small;
    case 'Component':
      return styles.colWidth.small;
    case 'Version':
      return styles.colWidth.small;
    case 'Active support':
      return styles.colWidth.small;
    case 'Security support':
      return styles.colWidth.small;
    case 'Result':
      return {
        minWidth: styles.colWidth.xlarge.minWidth,
      };
    case 'Name':
      return styles.colWidth.large;
    case 'Type':
      return styles.colWidth.medium;
    case 'Roadmap':
      return styles.colWidth.small;
    case 'Action':
      return styles.colWidth.medium;
    default:
      return styles.colWidth.large;
  }
};

export const formatCostKey = (
  costkey: CostResultKey
):
  | 'gts'
  | 'azure'
  | 'workforce'
  | 'non-workforce'
  | 'software'
  | 'aws'
  | undefined
  | 'server-gen-one'
  | 'server-gen-two' => {
  for (const key of [
    'gts',
    'azure',
    'workforce',
    'non-workforce',
    'software',
    'aws',
    'server-gen-one',
    'server-gen-two',
  ] as const) {
    if (costkey.split('_').at(-1)?.startsWith(key)) return key;
  }
};

export const formatColorByKey = (costkey: CostResultKey) => {
  const formattedCostkey = formatCostKey(costkey);

  const keyColourMap: { [key: string]: string } = {
    aws: '#B0A6C5',
    gts: '#058DC7',
    azure: '#494847',
    workforce: '#FF9655',
    'non-workforce': '#F5C653',
    software: '#50B432',
    'server-gen-one': '#065535',
    'server-gen-two': '#a0db8e',
    defaultcolour: '#6C3483',
  };

  return keyColourMap[formattedCostkey as string] || keyColourMap['defaultcolour'];
};

export const formatColorByLanguages = (index: number) => {
  const keyColourMap = [
    '#B0A6C5',
    '#058DC7',
    '#494847',
    '#FF9655',
    '#2a01a5',
    '#50B432',
    '#F5C653',
    '#6C1483',
    '#FF005D',
    '#006481',
    '#7a6464',
    '#395943',
    '#00dada',
    '#EA46DD',
    '#a62d59',
    '#b02c3a',
  ] as const;

  return keyColourMap[index % keyColourMap.length];
};

export const getRuleScopeKey = (resultKey: string): string => {
  switch (resultKey) {
    case 'web':
      return 'classic';
    case 'ci-cd':
      return 'digital-capability-ci-cd';
    default:
      return resultKey;
  }
};

const computeResultPresence = (resultType: IFilterResult): INode => {
  const isNist = resultType.key === 'digital-capability_security_nist';
  const children = PRESENCE_VALUE.filter((value) => (isNist ? value.key !== '0.5' : true)).map((presence) => {
    const computedNistValue = presence.key === '0' ? 'Not compliant' : 'Compliant';

    return {
      key: presence.key,
      label: isNist ? computedNistValue : presence.name,
      fullPath: `${resultType.key}/${presence.key}`,
    };
  });

  return {
    label: resultType.name ?? capitalizeEachFirstLetter(resultType.key),
    key: resultType.key,
    children,
    fullPath: resultType.key,
  };
};

const computeResultValue = (resultType: IFilterResult): INode => {
  const children = (resultType.values ?? []).map((value) => {
    return {
      key: value,
      label: value,
      fullPath: `${resultType.key}/${value}`,
    };
  });

  return {
    label: resultType.name ?? capitalizeEachFirstLetter(resultType.key),
    key: resultType.key,
    children,
    fullPath: resultType.key,
  };
};

export const computeResults = (resultType: IFilterResult[], componentRefs: IApplicationComponentRef[]): INode[] => {
  const resultTypeComputed = resultType.map((res) => {
    const currentComponentRef = componentRefs?.find((component) => component.key.includes(res.key));
    return { ...res, name: currentComponentRef?.name ?? res.name };
  });

  return resultTypeComputed.map((res) =>
    res.type === 'presence' ? computeResultPresence(res) : computeResultValue(res)
  );
};

export const computeRemediationResults = (
  remediationTarget: IRemediationComponent[],
  componentRefs: IApplicationComponentRef[]
): INode[] => {
  return [...(remediationTarget || [])]
    .sort((a, b) => a.scopeName?.localeCompare(b.scopeName))
    .map((res) => {
      const children = (res.components || []).map((component) => {
        const currentComponentRef = componentRefs?.find((componentRef) => componentRef.key.includes(component.key));

        return {
          key: currentComponentRef?.key ?? component.key,
          label: currentComponentRef?.name ?? component.name,
          fullPath: `${currentComponentRef?.key}`,
          children: component.versions?.map((version) => ({
            key: version,
            label: version,
            fullPath: `${currentComponentRef?.key}/${version}`,
          })),
        };
      });

      return {
        key: res.scopeKey,
        label: res.scopeName,
        children,
        fullPath: `scope-${res.scopeKey}`,
      };
    });
};

export const computeEnablers = (enablers: string[]): INode[] => {
  const formatLabel = (label: string): string => capitalizeFirstLetter(label.split('-').join(' '));

  return enablers?.reduce<INode[]>((acc, enabler) => {
    const [category, subcategory] = enabler.split(' - ');
    const formattedCategory = formatLabel(category);
    const formattedSubcategory = subcategory ? formatLabel(subcategory) : undefined;

    let categoryNode = acc.find((node) => node.key === category);
    if (!categoryNode) {
      categoryNode = {
        key: category,
        label: formattedCategory,
        children: [],
        fullPath: category,
      };
      acc.push(categoryNode);
    }

    if (formattedSubcategory) {
      categoryNode.children = categoryNode.children || [];
      if (!categoryNode.children.find((child) => child.key === subcategory)) {
        categoryNode.children.push({
          key: subcategory,
          label: formattedSubcategory,
          children: [],
          fullPath: `${category}/${subcategory}`,
        });
      }
    }

    return acc;
  }, []);
};

export const computeScopeRules = (resultType: IApplicationResult[], scopeRefs: IApplicationScopeRef[]): INode[] => {
  const scopesComputed = resultType.map((res) => {
    const currentScopeRef = scopeRefs?.find((scopeRef) => scopeRef.resultKeys.includes(res.key));
    return { ...res, name: res.name ?? currentScopeRef?.name };
  });

  return scopesComputed
    .filter((item) => item.key != 'health-changes')
    .map((scope) => {
      const isObsolescence = scope.key === 'obsolescence';

      const children = isObsolescence
        ? OBSOLESCENCE_VALUE.map((value) => {
            return {
              key: value.key,
              label: value.name,
              fullPath: `${scope.key}/${value.key}`,
              children: value.children.map((child) => {
                return { ...child, fullPath: `${scope.key}/${value.key}/${child.key}` };
              }),
            };
          })
        : RULE_VALUE.map((rule) => {
            return {
              key: rule.key,
              label: rule.name,
              fullPath: `${scope.key}/${rule.key}`,
            };
          });

      const isNova = scope.key === 'global_taxon_nova-compatible';
      const label = isNova ? 'Nova compatibility' : scope.name;

      return {
        label: label ?? capitalizeEachFirstLetter(scope.key),
        key: scope.key,
        children,
        fullPath: scope.key,
      };
    });
};

export const computeInfrastructureComponent = (infrastructureComponent: IInfrastructureComponent[]): INode[] => {
  const result = infrastructureComponent.flatMap(({ components, type }) => {
    const children = [...components]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(({ key, name, versions }) => {
        const children = (versions || []).map((version) => {
          return {
            key: version,
            label: version,
            fullPath: `${key}/${version}`,
          };
        });

        return {
          label: name,
          key,
          children,
          fullPath: key,
        };
      });
    return {
      label: type,
      key: type.toLowerCase(),
      children,
      fullPath: type,
    };
  });
  return result as INode[];
};

export const isNumber = (value: string) => {
  return /^\d+$/.test(value);
};

export const formatResult = (value: string | null | undefined, options?: { noDataLabel?: string }): string => {
  switch (value) {
    case 'Yes':
      return 'good';
    case 'No':
      return 'bad';
    case TO_BE_COMPLETED:
    case NorthStarPath.NO_DATA:
      return options?.noDataLabel ?? 'n/d';
    case NorthStarPath.NOT_APPLICABLE:
      return 'n/a';
    default:
      return value ?? options?.noDataLabel ?? 'n/d';
  }
};

export const formatNovaCompatibility = (novaCompatibility: NovaCompatibility | undefined): string => {
  switch (novaCompatibility) {
    case 'NO':
      return 'No';
    case 'TBC':
      return TO_BE_COMPLETED;
    case 'YES':
      return 'Yes';
    case 'n/a':
      return 'Not applicable';
    default:
      return 'No data';
  }
};
